import React, { FC, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import GeneralCommands from '../general-commands';
import Software from '../software';
import Subscription from '../subscription';
import ConfigurationFiles from '../configuration-files';
import PreferredNetworkAndOTASP from '../preferred-network-otasp';
import FaultCodes from '../fault-codes';
import WifiConfig from '../wifi-config';
import SimRecredential from '../sim-recredential';
import DeviceSettings from '../device-setting';
import { testIsPL240 } from '../../../globals/utils';
import Configuration from '../configuration';

type AccordionItem = { title: string; available: boolean; open: boolean; component: FC<{}> };
type AccordionData = { title: string; data: AccordionItem[] }[];

const ConfigurationAccordions: FC<{}> = () => {
  const deviceType = useSelector((state: AppState) => state.search.selectedEntity?.deviceType);
  const commercialType = useSelector((state: AppState) => state.search.selectedEntity?.commercialType);
  const isPl240 = testIsPL240(deviceType);
  const isP131orPL141 = () => commercialType === 'PL131' || commercialType === 'PL141';
  const sections = useMemo(
    () => [
      {
        title: 'Device Configuration',
        data: [
          {title: 'Configuration', available: isP131orPL141(), open: false, component: Configuration},
          { title: 'General Commands', available: true, open: false, component: GeneralCommands },
          {
            title: 'Configuration Files',
            available: !isPl240 && !isP131orPL141(),
            open: false,
            component: ConfigurationFiles,
          },
          {
            title: 'Subscription',
            available: !isPl240 && !isP131orPL141(),
            open: false,
            component: Subscription,
          },
          {
            title: 'Software',
            available: !isPl240 && !isP131orPL141(),
            open: false,
            component: Software,
          },
          {
            title: 'Preferred Network and OTASP',
            available: !isPl240 && !isP131orPL141(),
            open: false,
            component: PreferredNetworkAndOTASP,
          },
          {
            title: 'Fault Codes',
            available: !isP131orPL141(),
            open: false,
            component: FaultCodes,
          },
          {
            title: 'Wifi Configuration',
            available: !isP131orPL141(),
            open: false,
            component: WifiConfig,
          },
          {
            title: 'Sim Recredential',
            available: !isP131orPL141(),
            open: false,
            component: SimRecredential,
          },
          {
            title: 'Device Settings',
            available: true,
            open: false,
            component: DeviceSettings,
          },
        ],
      },
    ],
    [isPl240, deviceType],
  );

  const [accordionData, setAccordionData] = useState<AccordionData>(sections);

  useEffect(() => {
    setAccordionData(sections);
  }, [isPl240, deviceType]);

  const { showDeviceConfiguration } = useSelector((state: AppState) => state.commands);

  const toggleOpen = (sectionIterator: number, componentIterator: number) => {
    const toggledCopy = [...accordionData];
    toggledCopy[sectionIterator].data[componentIterator].open =
      !accordionData[sectionIterator].data[componentIterator].open;
    setAccordionData(toggledCopy);
  };

  if (showDeviceConfiguration)
    return (
      <>
        {accordionData.map((section, sectionIterator) => (
          <>
            <Box mt={3} mb={3}>
              <Typography variant="h6">{section.title}</Typography>
            </Box>
            {section.data.map(
              (componentContainer, componentIterator) =>
                componentContainer.available && (
                  <Accordion
                    style={{ position: 'static' }}
                    id={`${componentContainer.title}Panel`}
                    expanded={componentContainer.open}
                    onChange={() => toggleOpen(sectionIterator, componentIterator)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel2a-content"
                      id="panel2a-header"
                    >
                      <Typography variant="h5">{componentContainer.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {componentContainer.open && <componentContainer.component />}
                    </AccordionDetails>
                  </Accordion>
                ),
            )}
          </>
        ))}
      </>
    );
  return <></>;
};

export default ConfigurationAccordions;
